import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import WorkerNav from '@/components/WorkerNav';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User, Mail, Phone, MapPin, Building, Save, ArrowLeft, Globe, BellRing, BellOff } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const WorkerProfile = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { t, i18n } = useTranslation();
  const push = usePushNotifications({
    onOpen: (data) => {
      // Route into the app based on the tapped notification
      const relatedType = data?.related_type;
      const relatedId = data?.related_id;
      if (relatedType === 'field_log' && relatedId) {
        window.location.href = `/field-logs?highlight=${encodeURIComponent(relatedId)}`;
      } else if (relatedType === 'jsa' && relatedId) {
        window.location.href = `/jsa/${encodeURIComponent(relatedId)}`;
      } else if (relatedType === 'queue' && relatedId) {
        window.location.href = `/admin/jsa-update-queue`;
      }
    },
  });
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axiosInstance.get('/user/profile');
      setProfile({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        emergencyContact: response.data.emergencyContact || '',
        emergencyPhone: response.data.emergencyPhone || ''
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      // Use user prop as fallback
      setProfile(prev => ({
        ...prev,
        name: user?.name || '',
        email: user?.email || ''
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      await axiosInstance.patch('/user/profile', profile);
      toast.success(t('profile.updated'));
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error(t('profile.failedUpdate'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <WorkerNav user={user} onLogout={onLogout} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <WorkerNav user={user} onLogout={onLogout} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4 hover:bg-gray-100"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('common.back')}
        </Button>
        
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
          <p className="text-gray-600 mt-1">{t('profile.personalInfo')}</p>
        </div>

        {/* Profile Information */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-6 h-6 text-orange-600" />
              {t('profile.personalInfo')}
            </CardTitle>
            <CardDescription>{t('profile.updatePersonalDetails')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('common.fullName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      id="name"
                      value={profile.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="pl-10"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('common.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={profile.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="pl-10"
                      placeholder="john@example.com"
                      disabled
                    />
                  </div>
                  <p className="text-xs text-gray-500">{t('profile.emailCannotChange')}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('common.phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      id="phone"
                      value={profile.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="pl-10"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      id="address"
                      value={profile.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      className="pl-10"
                      placeholder="123 Main St, City, ST 12345"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-6 h-6 text-red-600" />
              Emergency Contact
            </CardTitle>
            <CardDescription>Person to contact in case of emergency</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergencyContact">Contact Name</Label>
                  <Input
                    id="emergencyContact"
                    value={profile.emergencyContact}
                    onChange={(e) => handleChange('emergencyContact', e.target.value)}
                    placeholder="Jane Doe"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="emergencyPhone">Contact Phone</Label>
                  <Input
                    id="emergencyPhone"
                    value={profile.emergencyPhone}
                    onChange={(e) => handleChange('emergencyPhone', e.target.value)}
                    placeholder="(555) 987-6543"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Language Preference */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-6 h-6 text-green-600" />
              {t('profile.languagePreference')}
            </CardTitle>
            <CardDescription>{t('profile.languageDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button
                variant={i18n.language?.startsWith('en') ? 'default' : 'outline'}
                onClick={() => {
                  i18n.changeLanguage('en');
                  localStorage.setItem('language', 'en');
                  axiosInstance.patch('/user/profile', { language: 'en' }).catch(() => {});
                  toast.success(t('profile.languageEnglish'));
                }}
                className={i18n.language?.startsWith('en') ? 'bg-orange-600 hover:bg-orange-700' : ''}
                data-testid="language-btn-en"
              >
                English
              </Button>
              <Button
                variant={i18n.language?.startsWith('es') ? 'default' : 'outline'}
                onClick={() => {
                  i18n.changeLanguage('es');
                  localStorage.setItem('language', 'es');
                  axiosInstance.patch('/user/profile', { language: 'es' }).catch(() => {});
                  toast.success('Idioma cambiado a Español');
                }}
                className={i18n.language?.startsWith('es') ? 'bg-orange-600 hover:bg-orange-700' : ''}
                data-testid="language-btn-es"
              >
                Español
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Push Alerts (only visible inside the native mobile app) */}
        {push.supported && (
          <Card className="mb-8" data-testid="push-alerts-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BellRing className="w-6 h-6 text-orange-600" />
                Field Alerts
              </CardTitle>
              <CardDescription>
                Get instant push notifications on this device for new field logs,
                incident reports, and JSA assignments — even when the app is closed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {push.permissionState !== 'granted' || !push.fcmToken ? (
                  <Button
                    onClick={push.registerForPush}
                    disabled={push.registering}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="enable-push-btn"
                  >
                    <BellRing className="w-4 h-4 mr-2" />
                    {push.registering ? 'Enabling…' : 'Enable Push Alerts'}
                  </Button>
                ) : (
                  <Button
                    onClick={push.unregisterForPush}
                    disabled={push.registering}
                    variant="outline"
                    data-testid="disable-push-btn"
                  >
                    <BellOff className="w-4 h-4 mr-2" />
                    {push.registering ? 'Disabling…' : 'Disable Push Alerts'}
                  </Button>
                )}
                <span className="text-xs text-gray-500">
                  Permission: <span className="font-medium">{push.permissionState}</span>
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Company Info (Read-only) */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-6 h-6 text-blue-600" />
              Company Information
            </CardTitle>
            <CardDescription>Your company and role details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm font-medium text-gray-600">Role</span>
                <span className="text-sm text-gray-900 capitalize">{user?.role || 'Worker'}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm font-medium text-gray-600">Company</span>
                <span className="text-sm text-gray-900">{user?.company || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-600">Member Since</span>
                <span className="text-sm text-gray-900">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={saveProfile}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default WorkerProfile;